import React, {FC, useRef} from 'react';

import {KeyboardActionsEnum} from '../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {useToggle} from '../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {PermissionCheck} from '../../../../srcCommon/permissions/permissionCheck';
import {useConversationStatusBulkActionPermission} from '../../../../srcCommon/permissions/useBulkActionPermission';
import {useConversationStatusActionOfflinePermission} from '../../../../srcCommon/permissions/useOfflinePermission';
import {TimeFormatsEnum} from '../../../helpers/dateTimeHelpers';
import {
  isConversationInTeamContext,
  makeFindConversationActionContext,
} from '../../../helpers/models/conversationHelpers';
import {PermissionResult} from '../../../helpers/permissionHelpers';
import {useAppDispatch} from '../../../helpers/reduxHelpers';
import {ConversationBaseModel} from '../../../models/api/conversationModel';
import {InboxModel} from '../../../models/api/inboxModel';
import {ReminderModel} from '../../../models/api/reminderModel';
import {PermissionsEnum} from '../../../models/api/roleModel';
import {setConversationsReminderAction} from '../../../store/data/conversations/reminderConversationsActions';
import {CellsContext} from '../../../store/panels/cells/context/cellsContext';
import {InboxesParameters} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {ViewerPanelId} from '../../../store/panels/panelStateBase';
import {SnoozeDropdownContainer} from '../viewer/conversation/actions/snooze/snoozeDropdownContainer';
import {makeInboxesTopbarSnoozeTooltipProps} from './inboxesTopbarCommon';
import {InboxesTopbarMainButton, InboxesTopbarMainButtonChevronProps} from './inboxesTopbarMainButton';

/*
 * Props.
 */

interface InboxesTopbarMainSnoozeProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  conversationsInboxes: ReadonlyArray<InboxModel>;
  conversations: ReadonlyArray<ConversationBaseModel>;
  remindersForAll: ReadonlyArray<ReminderModel>;
  timeFormat: TimeFormatsEnum;
  contextNamespaces: ReadonlyArray<string>;
  isBulkActionRequired: boolean;
}

/*
 * Component.
 */

export const InboxesTopbarMainSnooze: FC<InboxesTopbarMainSnoozeProps> = (props) => {
  const theme = useUserTheme();
  const {
    cellsContext,
    conversations,
    remindersForAll,
    contextNamespaces,
    isBulkActionRequired,
    inboxesParameters,
  } = props;
  const offlinePermission = useConversationStatusActionOfflinePermission(cellsContext, conversations);
  const bulkActionPermission = useConversationStatusBulkActionPermission(
    isBulkActionRequired,
    inboxesParameters,
  );
  const [isSnoozeDropdownOpen, toggleIsSnoozeDropdownOpen] = useToggle();
  const mainButtonRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  const onUnsnoozeClick = () => {
    // TODO: Handle the bulk case.
    if (isBulkActionRequired) {
      return;
    }

    dispatch(
      setConversationsReminderAction(
        cellsContext,
        conversations.map((c) => c.id),
        null,
        makeFindConversationActionContext(cellsContext, conversations),
      ),
    );
  };

  const onSnoozeClick = () => {
    toggleIsSnoozeDropdownOpen();
  };

  function renderButton(permissionResult: PermissionResult) {
    const tooltipProps = makeInboxesTopbarSnoozeTooltipProps(props);
    const icon = remindersForAll ? (
      <Icon color={theme.palette.orange.shade40} name="snoozeFilled" />
    ) : (
      <Icon name="snooze" />
    );
    const chevronProps: InboxesTopbarMainButtonChevronProps = {
      renderDropdown: (modalProps) => (
        <SnoozeDropdownContainer
          {...modalProps}
          cellsContext={props.cellsContext}
          inboxesParameters={props.inboxesParameters}
          conversations={props.conversations}
          isBulkActionRequired={props.isBulkActionRequired}
        />
      ),
      tooltipFirstLine: <Trans id="conversation-actions-updatesnooze">Update snooze</Trans>,
    };

    return (
      <InboxesTopbarMainButton
        ref={mainButtonRef}
        isDisabled={!permissionResult.isAllowed}
        onClick={remindersForAll ? onUnsnoozeClick : onSnoozeClick}
        keyboardAction={KeyboardActionsEnum.SNOOZE}
        tooltipFirstLine={tooltipProps.firstLine || tooltipProps.mainButton}
        tooltipSecondLine={tooltipProps.secondLine}
        chevronProps={remindersForAll ? chevronProps : undefined}
        icon={icon}
      >
        {makeSnoozedButtonText(props)}
      </InboxesTopbarMainButton>
    );
  }

  return (
    <>
      {isSnoozeDropdownOpen && (
        <SnoozeDropdownContainer
          anchor={mainButtonRef}
          onRequestClose={toggleIsSnoozeDropdownOpen}
          cellsContext={props.cellsContext}
          inboxesParameters={props.inboxesParameters}
          conversations={props.conversations}
          isBulkActionRequired={props.isBulkActionRequired}
          placement="bottom-end"
        />
      )}
      <PermissionCheck
        namespaces={contextNamespaces}
        permission={PermissionsEnum.CONVERSATION_ARCHIVE}
        bulkActionPermission={bulkActionPermission}
        offlinePermission={offlinePermission}
      >
        {(permissionResult) => renderButton(permissionResult)}
      </PermissionCheck>
    </>
  );
};

function makeSnoozedButtonText(props: InboxesTopbarMainSnoozeProps) {
  const {cellsContext, conversationsInboxes, conversations} = props;

  const areAllInTeamContext = conversations.every((c) => isConversationInTeamContext(cellsContext, c));

  if (cellsContext.isDelegation) {
    return <Trans id="conversation-actions-unsnoozeinteammate">Unsnooze in teammate inbox</Trans>;
  }

  if (areAllInTeamContext) {
    if (conversationsInboxes.length === 1) {
      return (
        <Trans id="conversation-actions-unsnoozeininbox">Unsnooze in {conversationsInboxes[0].name}</Trans>
      );
    }

    return (
      <Trans id="conversation-actions-unsnoozeinmany">
        Unsnooze in {conversationsInboxes.length} inboxes
      </Trans>
    );
  }

  return <Trans id="conversation-actions-unsnoozeinmy">Unsnooze in my inbox</Trans>;
}
